import {
  useCallback,
  useRef,
  useState,
  useEffect,
  ReactEventHandler,
  useMemo,
} from 'react'
import { Schemas } from '~/api/gen'
import { CloseButton } from '~/components/elements/CloseButton'
import { ImageOffIcon } from '~/components/icons/generated/ImageOffIcon'
import { ImageSearchCloseType } from '~/features/shared/gtmEvent/types/coordinateDetail'
import { useBinary } from '~/hooks/useBinary'
import { Gender } from '~/lib/gender/types'
import { ImageSearchItem } from '../ImageSearchItem/ImageSearchItem'
import { ImageSearchList } from './ImageSearchList'
import { NotFoundSimilarItem } from './NotFoundSimilarItem'
import { SimilarItemList } from './SimilarItemList'

/* eslint-disable complexity */
export const ImageSearchResult: React.FC<{
  imageContainerHeight: number
  imgUrl: string
  isValidating: boolean
  imageSearchKeys: Schemas.image_search_key[] | undefined
  coordinateId: number
  onCloseClick?: () => void
  closeButtonGTMEvent: (eventElm: ImageSearchCloseType) => void
  setEventLabel: (searchConditions: string) => void
}> = ({
  imageContainerHeight,
  imgUrl,
  isValidating,
  imageSearchKeys,
  coordinateId,
  onCloseClick,
  closeButtonGTMEvent,
  setEventLabel,
}) => {
  const imgRef = useRef<HTMLImageElement>(null)
  const [isImageLoaded, imageLoaded] = useBinary(false)
  const [imageSearchContainerSize, setImageSearchContainerSize] = useState({
    width: 0,
    height: 0,
  })
  const handleImageLoaded = useCallback<ReactEventHandler<HTMLImageElement>>(
    (e) => {
      imageLoaded()
      setImageSearchContainerSize({
        width: e.currentTarget.offsetWidth,
        height: e.currentTarget.offsetHeight,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const [showingItem, setShowingItem] = useState<{
    searchKey?: string
    isBalloonShow: boolean
  }>({
    isBalloonShow: false,
  })
  const updateShowingItem = useCallback(
    (searchKey: string) => () => {
      setShowingItem({
        searchKey,
        isBalloonShow:
          searchKey !== showingItem.searchKey || !showingItem.isBalloonShow,
      })
    },
    [showingItem.isBalloonShow, showingItem.searchKey]
  )

  useEffect(() => {
    if (imageSearchKeys === undefined || imageSearchKeys.length === 0) {
      setShowingItem({ isBalloonShow: false })
      return
    }
    setShowingItem((prev) => ({
      ...prev,
      searchKey: imageSearchKeys[0]?.search_key ?? '',
    }))
    setTimeout(() => {
      setShowingItem((prev) => ({ ...prev, isBalloonShow: true }))
    }, 600)
  }, [imageSearchKeys])

  const imageSearchKey = useMemo(
    () =>
      imageSearchKeys?.find((key) => key.search_key === showingItem.searchKey),
    [imageSearchKeys, showingItem.searchKey]
  )

  const [currentGender, setCurrentGender] = useState<Gender>('all')

  return (
    <div className="relative bg-white">
      <div className="sticky left-0 top-0">
        <div
          className="w-full bg-white text-center"
          style={{ height: `${imageContainerHeight}px` }}
        >
          <img
            src={imgUrl}
            ref={imgRef}
            className="mx-auto my-0 h-full w-auto"
            alt="コーデ画像"
            onLoad={handleImageLoaded}
          />
        </div>
        {isImageLoaded && isValidating && (
          <div className="absolute left-0 top-0 z-[1] flex size-full justify-center">
            <div className="absolute left-1/2 top-1/2 ml-[-26px] mt-[-49px] size-[52px] animate-spin rounded-full border-[3px] border-solid border-y-[rgba(255,255,255,0.2)] border-l-white border-r-[rgba(255,255,255,0.2)]" />
            <p className="absolute left-0 top-1/2 mt-4 w-full text-center text-[11px] font-bold leading-normal text-white">
              似ているアイテムを
              <br />
              探しています..
            </p>
          </div>
        )}
        {isImageLoaded && imageSearchKeys && imageSearchKeys.length > 0 && (
          <ul
            className="absolute left-1/2 top-0"
            style={{
              marginLeft: `-${imageSearchContainerSize.width / 2}px`,
              width: `${imageSearchContainerSize.width}px`,
              height: `${imageSearchContainerSize.height}px`,
            }}
          >
            {imageSearchKeys.map(
              (key) =>
                imgRef.current !== null && (
                  <ImageSearchItem
                    key={key.search_key}
                    img={imgRef.current}
                    containerSize={imageSearchContainerSize}
                    imageSearchKey={key}
                    isShow={
                      showingItem.searchKey === key.search_key &&
                      showingItem.isBalloonShow
                    }
                    onClick={updateShowingItem}
                  />
                )
            )}
          </ul>
        )}
        {isImageLoaded &&
          !isValidating &&
          (!imageSearchKeys || imageSearchKeys?.length === 0) && (
            <span className="absolute left-0 top-0 z-10 flex size-full items-center justify-center">
              <ImageOffIcon
                aria-label="カテゴリーが検出されませんでした"
                className="h-[46px] text-white"
              />
            </span>
          )}
        <div className="absolute left-0 top-0 h-[calc(100%+16px)] w-full bg-black/30">
          <CloseButton
            onClick={() => {
              closeButtonGTMEvent('snap')
              onCloseClick?.()
            }}
            className="absolute left-[2px] top-[2px] z-10 p-[10px] text-[23px] text-white"
          />
        </div>
      </div>
      <ImageSearchList
        onCloseClick={() => {
          closeButtonGTMEvent('modal')
          onCloseClick?.()
        }}
        onGenderChange={setCurrentGender}
        gender={currentGender}
      >
        {imageSearchKey !== undefined ? (
          <SimilarItemList
            imageSearchKey={imageSearchKey}
            gender={currentGender}
            coordinateId={coordinateId}
            setEventLabel={setEventLabel}
          />
        ) : (
          imageSearchKeys !== undefined && (
            <NotFoundSimilarItem className="p-[10px] pt-[39px]" />
          )
        )}
      </ImageSearchList>
    </div>
  )
}
